import {Globals} from '../globals';
import {isKnownBaseCategory} from '../routing/base-category-registry';
import {BreadcrumbService} from "xng-breadcrumb";
import {formatBrowseSegmentLabel} from './browse-seo.utils';
import {getBreadcrumbAlias} from '../category/shared/category-level/category-level.config';

/**
 * Capitalizes the first letter of each word in a sentence.
 *
 * @returns The sentence with each word capitalized.
 * @param urls
 * @param globals
 */
// Browse URLs come in two shapes:
//   direct  : /<category>/<sub>/<subsub>/...           (urls[0] is the base category)
//   profile : /<profileId>/<category>/<sub>/<subsub>/... (urls[0] is the profile)
// Profiles can be mounted under ANY vertical (account.routing uses a dynamic
// ":category" child), so we can no longer rely on a literal "motors" marker.
// Instead a first segment that is not a registered base category must be a
// profile id, because the router only falls through to the account ":id" route
// when the first segment matches no known top-level category.
const FILTER_CHAIN_KEYS = [
  'subcategory',
  'subSubcategory',
  'subSubSubcategory',
  'subSubSubSubcategory',
  'subSubSubSubSubcategory',
  'subSubSubSubSubSubcategory',
  'subSubSubSubSubSubSubcategory',
  'subSubSubSubSubSubSubSubcategory',
] as const;

const PLATFORM_ROUTE_SLUGS = new Set([
  'home', 'add-listing', 'add-item', 'update-item', 'item', 'items',
  'signup', 'login', 'profile', 'account', 'admin', 'payment', 'messaging', 'compare',
]);

export function isProfileUrl(urls: any): boolean {
  if (!urls || urls.length < 2) {
    return false;
  }
  const first = `${urls[0] ?? ''}`.toLowerCase();
  if (PLATFORM_ROUTE_SLUGS.has(first)) {
    return false;
  }
  // A known base category as the first segment is always a direct browse.
  if (isKnownBaseCategory(urls[0])) {
    return false;
  }
  // Legacy "motors" marker (kept so existing motors profile links still parse
  // even if the registry hasn't been populated yet).
  if (urls[1] === 'motors' || urls[1] === 'all-motors') {
    return true;
  }
  // Otherwise the first segment isn't a category, so it must be a profile id.
  return true;
}

/** Profile slug from URL segments, including shop root /:profileId. */
export function resolveProfileId(urls: string[] | null | undefined): string | null {
  if (!urls?.length) {
    return null;
  }
  if (isProfileUrl(urls)) {
    return urls[0];
  }
  if (urls.length === 1) {
    const slug = urls[0];
    const lower = slug.toLowerCase();
    if (isKnownBaseCategory(slug) || PLATFORM_ROUTE_SLUGS.has(lower)) {
      return null;
    }
    return slug;
  }
  return null;
}

/**
 * Resolves the top-level (base) category slug from the current URL segments,
 * accounting for the optional profile prefix. Lets browse pages work for any
 * vertical (e.g. a generic "test" category) instead of being pinned to motors.
 */
export function resolveBaseCategory(urls: any): string {
  if (!urls || !urls.length) {
    return '';
  }
  let offset = isProfileUrl(urls) ? 1 : 0;
  while (offset < urls.length) {
    const seg = `${urls[offset] ?? ''}`.toLowerCase();
    if (seg === 'add-listing' || seg === 'add-item' || seg === 'update-item') {
      offset++;
      continue;
    }
    break;
  }
  return (urls[offset] || '').replaceAll('all-', '');
}

export function globalsBrowseFilter(urls: any, globals: Globals): string {
  if (urls?.[0] === 'add-listing') {
    return globalsAddListingFilter(urls, globals);
  }

  let urlName = '';
  const profile = isProfileUrl(urls);
  const offset = profile ? 1 : 0;

  if (profile) {
    globals.listingFilter.profile = urls[0];
  } else {
    globals.listingFilter.profile = null;
  }

  globals.listingFilter.category = (urls[offset] || '').replaceAll('all-', '');

  // Reset taxonomy chain before applying the current URL so drill-up navigation
  // does not leave stale deeper filters (e.g. subSubcategory=test4 on /test3/wow).
  for (const key of FILTER_CHAIN_KEYS) {
    (globals.listingFilter as any)[key] = null;
  }

  for (let i = 0; i < FILTER_CHAIN_KEYS.length; i++) {
    const segment = urls[offset + 1 + i];
    if (!segment || segment === 'all') {
      break;
    }

    // Breadcrumb label (human readable)
    if (i === 0) {
      urlName = segment.replaceAll('-', ' ').replaceAll(' and', ' &');
    } else {
      urlName = segment;
    }
    // Search/API filters use URL slugs from the path verbatim
    (globals.listingFilter as any)[FILTER_CHAIN_KEYS[i]] = segment;
  }

  return urlName;
}

/**
 * Path segments for category taxonomy only. On item-detail routes the last
 * segment is the item slug/id and must not be stored as subSubSubcategory, etc.
 */
export function taxonomyUrlSegments(fullPath: string, itemId?: string | null): string[] {
  const segments = fullPath.split('?')[0].split('/').filter(Boolean);
  if (!itemId || segments.length === 0) {
    return segments;
  }
  if (segments[segments.length - 1] === itemId) {
    return segments.slice(0, -1);
  }
  return segments;
}

/** Human-readable breadcrumb label from a URL slug (matches category-level browse). */
export function formatBrowseBreadcrumbLabel(slug: string): string {
  const normalized = (slug || '').replace(/^all-/, '');
  return formatBrowseSegmentLabel(normalized);
}

/**
 * Restores browse taxonomy breadcrumb labels from the URL. Item-detail routes
 * only mount ListingBrowseItemComponent, so parent category components never run
 * setBreadcrumb().
 *
 * Uses route paths (not shared aliases like @para / @paramotor) so each taxonomy
 * level gets its own label. Skips @paramotor, which can appear as a duplicate
 * crumb alongside the merged depth alias on the same segment.
 */
export function applyBrowseTaxonomyBreadcrumbs(
  breadcrumbService: BreadcrumbService,
  fullPath: string,
  itemId?: string | null,
): void {
  const pathOnly = fullPath.split('?')[0];
  const urls = taxonomyUrlSegments(pathOnly, itemId);
  if (!urls.length) {
    return;
  }

  const profile = isProfileUrl(urls);
  const offset = profile ? 1 : 0;

  if (profile) {
    breadcrumbService.set('@Account', formatBrowseBreadcrumbLabel(urls[0]));
  }

  const normalizedSegments = urls
    .slice(offset)
    .map(segment => segment.replace(/^all-/, ''))
    .filter(segment => segment && segment !== 'all');

  let routePrefix = profile ? `/${urls[0]}` : '';
  normalizedSegments.forEach((segment, index) => {
    routePrefix += `/${segment}`;
    const label = formatBrowseBreadcrumbLabel(segment);
    if (index === 0) {
      // Base category landing uses @Category / @Motors.
      breadcrumbService.set('@Category', label);
      breadcrumbService.set('@Motors', label);
    } else {
      // Nested browse depths start at 2 (see createBrowseLevelRoutes / getBreadcrumbAlias).
      const depth = index + 1;
      if (depth >= 2 && depth <= 8) {
        breadcrumbService.set(`@${getBreadcrumbAlias(depth)}`, label);
      }
    }
    // Path-keyed labels cover xng crumbs that resolve by route URL.
    breadcrumbService.set(routePrefix, label);
    // Listings / detail leaf alias used by several browse routes.
    if (index === normalizedSegments.length - 1) {
      breadcrumbService.set('@para', label);
      breadcrumbService.set('@par', label);
    }
  });

  breadcrumbService.set('@paramotor', {skip: true});
}

/** Taxonomy slugs from an add-listing URL (strips platform slugs, all-, and trailing /all). */
export function parseAddListingTaxonomySegments(fullPath: string): string[] {
  const segments = fullPath.split('?')[0].split('/').filter(Boolean);
  if (segments[0] !== 'add-listing') {
    return [];
  }

  const result: string[] = [];
  for (const raw of segments.slice(1)) {
    if (!raw || raw === 'all' || ADD_LISTING_PLATFORM_SLUGS.has(raw.toLowerCase())) {
      continue;
    }
    result.push(raw.startsWith('all-') ? raw.slice(4) : raw);
  }
  return result;
}

export interface AddListingBreadcrumbItem {
  label: string;
  routeLink: string;
}

/** Full add-listing breadcrumb trail from the URL (independent of xng route aliases). */
export function buildAddListingBreadcrumbTrail(fullPath: string): AddListingBreadcrumbItem[] {
  const pathOnly = fullPath.split('?')[0];
  if (!pathOnly.includes('/add-listing')) {
    return [];
  }

  const segments = parseAddListingTaxonomySegments(pathOnly);
  const isLeafForm = /\/all(?:[/?#]|$)/.test(pathOnly);
  const trail: AddListingBreadcrumbItem[] = [{label: 'Add Listing', routeLink: '/add-listing'}];

  let routePrefix = '/add-listing';
  segments.forEach((segment, index) => {
    routePrefix += `/${segment}`;
    const isLast = index === segments.length - 1;
    const routeLink = isLast && isLeafForm ? `${routePrefix}/all` : routePrefix;
    trail.push({
      label: formatBrowseBreadcrumbLabel(segment),
      routeLink,
    });
  });

  return trail;
}

/**
 * Restores add-listing taxonomy breadcrumb labels from the URL. Only the leaf
 * category-level or form route is mounted, so parent aliases are never updated
 * by setBreadcrumb() on ancestor components.
 *
 * Uses route paths so every taxonomy segment gets a label. Clears the global
 * @paramotor skip left by applyBrowseTaxonomyBreadcrumbs — the parent segment
 * before a leaf form (e.g. test8 before test9) is the only visible @paramotor
 * crumb and is hidden while that skip is active.
 */
export function applyAddListingTaxonomyBreadcrumbs(
  breadcrumbService: BreadcrumbService,
  fullPath: string,
): void {
  const pathOnly = fullPath.split('?')[0];
  if (!pathOnly.includes('/add-listing')) {
    return;
  }

  const segments = parseAddListingTaxonomySegments(pathOnly);
  breadcrumbService.set('@AddItem', 'Add Listing');
  // Browse item-detail sets this globally in the xng-breadcrumb store; it hides the
  // parent drill-down crumb before a leaf form (e.g. test8 before test9).
  breadcrumbService.set('@paramotor', {skip: false});

  if (!segments.length) {
    return;
  }

  let routePrefix = '/add-listing';
  segments.forEach(segment => {
    routePrefix += `/${segment}`;
    breadcrumbService.set(routePrefix, {
      label: formatBrowseBreadcrumbLabel(segment),
      skip: false,
    });
  });
}

export function globalsAddListingFilter(urls: any, globals: Globals): string {
  let urlName = '';
  if (urls[0] === 'add-listing') {
    globals.listingFilter.category = null;
    globals.listingFilter.subcategory = null;
    globals.listingFilter.subSubcategory = null;
    globals.listingFilter.subSubSubcategory = null;
    globals.listingFilter.subSubSubSubcategory = null;
    globals.listingFilter.subSubSubSubSubcategory = null;
    globals.listingFilter.subSubSubSubSubSubcategory = null;
    globals.listingFilter.subSubSubSubSubSubSubcategory = null;
    globals.listingFilter.subSubSubSubSubSubSubSubcategory = null;

    // /add-listing → base categories; /add-listing/test → subcategories of test; etc.
    let idx = urls[1] === 'add-item' ? 2 : 1;
    while (idx < urls.length && ADD_LISTING_PLATFORM_SLUGS.has(`${urls[idx] ?? ''}`.toLowerCase())) {
      idx++;
    }
    const filterKeys = [
      'category',
      'subcategory',
      'subSubcategory',
      'subSubSubcategory',
      'subSubSubSubcategory',
      'subSubSubSubSubcategory',
      'subSubSubSubSubSubcategory',
      'subSubSubSubSubSubSubcategory',
      'subSubSubSubSubSubSubSubcategory',
    ] as const;

    for (const key of filterKeys) {
      const raw = urls[idx];
      if (!raw || raw === 'all') {
        break;
      }
      const segment = raw.startsWith('all-') ? raw.slice(4) : raw;
      // API filters use URL slugs from the path, not humanized labels.
      globals.listingFilter[key] = segment;
      urlName = segment;
      idx++;
    }

    return getParamsFromUrl(urlName);
  } else  {

    globals.listingFilter.profile = urls[0];
    globals.listingFilter.category = urls[1];
    globals.listingFilter.subcategory = urls[2].replaceAll('-', ' ');
    globals.listingFilter.subcategory = globals.listingFilter.subcategory.replaceAll(' and', ' &');
    urlName = globals.listingFilter.subcategory;
    if (urls[3] && urls[3] !== 'all') {
      globals.listingFilter.subSubcategory = getParamsFromUrl(urls[3]);
      urlName = urls[3];
      if (urls[4] && urls[4] !== 'all') {
        globals.listingFilter.subSubSubcategory = getParamsFromUrl(urls[4]);
        urlName = urls[4];
        if (urls[5] && urls[5] !== 'all') {
          urlName = urls[5];
          globals.listingFilter.subSubSubSubcategory = getParamsFromUrl(urls[5]);
        }
      }
    }
    return urlName;
  }

}

function isUuid(str: string): boolean {
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  return uuidRegex.test(str);
}

export function globalsUpdateListingFilter(urls: any, globals: Globals): string {
  let urlName = '';
  if (urls[0] === 'update-item' || urls[0] === 'add-listing') {
    globals.listingFilter.category = urls[0];

    globals.listingFilter.subcategory = urls[1].replaceAll('-', ' ');
    globals.listingFilter.subcategory = globals.listingFilter.subcategory.replaceAll(' and', ' &');
    urlName = globals.listingFilter.subcategory;
    if (urls[2] && urls[2] !== 'all') {
      if(isUuid(urls[2])) {
        globals.listingFilter.id = urls[2];
      } else {
        globals.listingFilter.subSubcategory = getParamsFromUrl(urls[2]);
        urlName = urls[2];
      }

      if (urls[3] && urls[3] !== 'all') {
        if(isUuid(urls[3])) {
          globals.listingFilter.id = urls[3];
        } else {
          globals.listingFilter.subSubSubcategory = getParamsFromUrl(urls[3]);
          urlName = urls[3];
        }
        if (urls[4] && urls[4] !== 'all') {
          if(isUuid(urls[4])) {
            globals.listingFilter.id = urls[4];
          } else {
            globals.listingFilter.subSubSubSubcategory = getParamsFromUrl(urls[4]);
            urlName = urls[4];
          }
          if (urls[5] && urls[5] !== 'all') {
            if (isUuid(urls[5])) {
              globals.listingFilter.id = urls[5];
            } else {
              globals.listingFilter.subSubSubSubSubcategory = getParamsFromUrl(urls[5]);
              urlName = urls[5];
            }
          }
        }
      }
    }
    return urlName;

  }

}



const ADD_LISTING_PLATFORM_SLUGS = new Set([
  'add-listing', 'home', 'item', 'items', 'login', 'profile', 'signup', 'update-item',
  'add-item', 'payment', 'messaging', 'compare', 'admin', 'account',
]);

/** Convert a browse taxonomy URL to the matching add-listing URL (same slug trail + /all). */
export function browsePathToAddListingPath(browsePath: string): string {
  const segments = browsePath.split(/[?#]/)[0].split('/').filter(Boolean);
  const skip = new Set(['all', 'home', 'item', 'items', 'login', 'profile', 'signup', 'add-listing', 'add-item', 'update-item']);
  const taxonomy: string[] = [];
  for (const segment of segments) {
    const lower = segment.toLowerCase();
    if (skip.has(lower)) {
      continue;
    }
    if (lower.startsWith('all-')) {
      taxonomy.push(segment.slice(4));
      continue;
    }
    taxonomy.push(segment);
  }
  if (!taxonomy.length) {
    return '/add-listing';
  }
  return `/add-listing/${taxonomy.join('/')}/all`;
}

/** Normalize add-listing URLs: drop platform slugs; never rewrite generic …/slug/all to …/all-slug/all. */
export function canonicalAddListingRouterUrl(fullUrl: string): string | null {
  const hashIdx = fullUrl.indexOf('#');
  const hash = hashIdx >= 0 ? fullUrl.slice(hashIdx) : '';
  const withoutHash = hashIdx >= 0 ? fullUrl.slice(0, hashIdx) : fullUrl;
  const qIdx = withoutHash.indexOf('?');
  const query = qIdx >= 0 ? withoutHash.slice(qIdx) : '';
  const path = qIdx >= 0 ? withoutHash.slice(0, qIdx) : withoutHash;
  const segments = path.split('/').filter(Boolean);
  if (segments[0] !== 'add-listing') {
    return null;
  }

  const out: string[] = ['add-listing'];
  for (let i = 1; i < segments.length; i++) {
    const seg = segments[i];
    if (ADD_LISTING_PLATFORM_SLUGS.has(seg.toLowerCase())) {
      continue;
    }
    out.push(seg);
  }

  if (out.length >= 3 && out[out.length - 1] === 'all') {
    const slug = out[out.length - 2];
    if (slug?.startsWith('all-')) {
      out.splice(out.length - 2, 1, slug.slice(4));
    }
  }

  const canonical = `/${out.join('/')}${query}${hash}`;
  return canonical === fullUrl ? null : canonical;
}

/** Clears profile-scoped filters so add-listing uses site-wide taxonomy. */
export function clearAddListingBrowseFilters(globals: Globals): void {
  globals.listingFilter.profile = null;
  globals.listingFilter.isProfileLoggedIn = null;
  globals.listingFilter.category = null;
  globals.listingFilter.subcategory = null;
  globals.listingFilter.subSubcategory = null;
  globals.listingFilter.subSubSubcategory = null;
  globals.listingFilter.subSubSubSubcategory = null;
  globals.listingFilter.subSubSubSubSubcategory = null;
  globals.listingFilter.subSubSubSubSubSubcategory = null;
  globals.listingFilter.subSubSubSubSubSubSubcategory = null;
  globals.listingFilter.subSubSubSubSubSubSubSubcategory = null;
}

/** Whether a /items/categories row should render on the add-listing picker. */
export function isAddListingRootCategoryCard(item: any[]): boolean {
  if (!item?.[1]) {
    return false;
  }
  const slug = `${item[1]}`;
  if (ADD_LISTING_PLATFORM_SLUGS.has(slug)) {
    return false;
  }
  return isKnownBaseCategory(slug);
}

 function getParamsFromUrl(urlParam: any) {
  let url = urlParam;
  url = url.replace('-and-', ' and ');
  // url = url.replace('-', ' ');
  let val = url;
  if (val.includes(' and ')) {
    val = val.replace(' and ', ' & ');
  }
  if (val.includes('-')) {
    val = val.replace('-', ' ');
  }
  return val;
}
